import './App.css';
import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import JoinPage from './pages/JoinPage';
import DonatePage from './pages/DonatePage';
import CampaignPage from './pages/CampaignPage';
import PetitionPage from './pages/PetitionPage';
import InternationalPage from './pages/InternationalPage';
import CookieConsent from './components/CookieConsent';

// Campaign pages used to live at /campaign/:campaignName. Links shared under
// that shape are already out in the world, so keep them working by forwarding
// to the single-segment URL with the slug intact — CampaignPage's own
// unknown-slug check sends it home from there if the slug is no longer valid.
// The query string carries ?donation=recurring, which CampaignPage reads on
// mount, so it has to survive the hop.
function LegacyCampaignRedirect() {
  const { campaignName } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${campaignName}${search}${hash}`} replace />;
}

function App() {
  const location = useLocation();

  useEffect(() => {
    // Google: page view conversion on every route
    if (window.gtag) window.gtag('event', 'conversion', { send_to: 'AW-718777808/UDgTCNmWiO0bENDb3tYC' });

    // Meta: ViewContent after 20 seconds of time on the current page
    const timer = setTimeout(() => {
      if (window.fbq) window.fbq('track', 'ViewContent');
    }, 20000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // donate.standing-together.org serves DonatePage at "/"; every other host
  // (incl. join.standing-together.org and localhost) serves JoinPage at "/".
  // "/donate" keeps working on any host so existing links never break.
  const isDonateHost = window.location.hostname.startsWith('donate.') || window.location.hostname.startsWith('www.donate.');
  // petition.standing-together.org serves the petition for /:slug directly -
  // /test instead of /petitions/test - so a petition link is one segment.
  const isPetitionHost = window.location.hostname.startsWith('petition.') || window.location.hostname.startsWith('www.petition.');

  return (
    <div className="App" dir="rtl" lang="he">
      <Routes>
        {/* Both pages are full-bleed landing layouts (fixed purple panel) — no navbar */}
        <Route path="/" element={isDonateHost ? <DonatePage /> : <JoinPage />} />
        <Route path="/donate" element={<DonatePage />} />
        {/* English donate page for donors abroad. A static segment, so it
            outranks the /:campaignName and /:slug catch-alls below. */}
        <Route path="/en" element={<InternationalPage />} />
        {/* Pre-existing campaign URL shape — forwarded, never rendered */}
        <Route path="/campaign/:campaignName" element={<LegacyCampaignRedirect />} />
        {/* Per-petition landing page. The plural /petitions/ matches the pages
            this replaces (standing-together.org/petitions/:slug), so links
            already shared keep working. The fetched record's project_type is
            still the authority, and each page redirects to the other when the
            two disagree. Declared before the root catch-all for readability —
            the two can't collide (different segment counts). */}
        <Route path="/petitions/:slug" element={<PetitionPage />} />
        {/* Single-segment project URL. On the petition host this is a petition
            slug (petition.example.org/test); everywhere else it is a campaign
            slug. */}
        {isPetitionHost
          ? <Route path="/:slug" element={<PetitionPage />} />
          : <Route path="/:campaignName" element={<CampaignPage />} />}
        {/* Anything left over is a multi-segment path no route claims. Without
            this, <Routes> renders nothing and the user gets a white screen. */}
        <Route path="*" element={<Navigate to="/donate" replace />} />
      </Routes>
      <CookieConsent />
    </div>
  );
}

export default App;
